import * as React from "react";
import { Box, Button, Paper, Typography, Stack, Chip, Divider } from "@mui/material";
import dayjs from "dayjs";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CancelIcon from "@mui/icons-material/Cancel";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { BILL_TYPES, BILL_TYPE_META, BACKEND_KEY } from "../../../constants/billTypes";
import { calculateLatePenalty } from "../../../utils/latePolicy";

// Reads a canonical bill type's uploaded record straight off the raw
// GetTenantCurrentBill payload (same field-name convention CurrentBill.js
// itself uses, via BACKEND_KEY).
function readBillRecord(billData, canonicalType) {
  const key = BACKEND_KEY[canonicalType];
  if (!key || !billData) return null;
  return billData?.[key] === true ? billData?.[`${key}_data`]?.[0] ?? null : null;
}

function statusChip(status) {
  if (status === "approved") {
    return <Chip size="small" icon={<CheckCircleIcon />} label="Approved" color="success" />;
  }
  if (status === "rejected") {
    return <Chip size="small" icon={<CancelIcon />} label="Rejected" color="error" />;
  }
  if (status) {
    return <Chip size="small" icon={<HourglassEmptyIcon />} label="Pending" color="warning" />;
  }
  return <Chip size="small" icon={<InfoOutlinedIcon />} label="Not uploaded yet" variant="outlined" />;
}

// Rent "review due" is purely informational — non-blocking, and hidden
// entirely if we don't have enough data to say something honest.
//
// The interval is the lease term the owner set at registration, which the
// cycle payload reports as `leaseTermMonths`. It used to be hardcoded to one
// year, so an 11-month lease was warned about a month late and a 6-month one
// twice as late. Anchored to the tenancy start, not the property's createdAt.
function useRentReviewChip(flatDetail, cycle) {
  return React.useMemo(() => {
    const increasePercent = Number(cycle?.rentIncreasePercent ?? flatDetail?.rentIncrease);
    const termMonths = Number(cycle?.leaseTermMonths);
    const anchor = flatDetail?.acceptedDate || flatDetail?.createdAt;

    // A flexible lease has no term, so there is no review date to announce.
    if (!anchor || !increasePercent || !termMonths) return null;

    const start = dayjs(anchor);
    if (!start.isValid()) return null;

    const now = dayjs();
    let nextReview = start.add(termMonths, "month");
    while (nextReview.isBefore(now, "day")) {
      nextReview = nextReview.add(termMonths, "month");
    }
    const daysLeft = nextReview.diff(now, "day");
    if (daysLeft > 60) return null; // only surface it as it approaches

    return `Rent review (+${increasePercent}%) due in ${daysLeft} day${daysLeft === 1 ? "" : "s"}`;
  }, [flatDetail, cycle]);
}

// Which canonical types are paid online from this screen. The utilities and
// maintenance are paid the way they always were (through their own dialogs).
const ONLINE_PAYABLE = {
  [BILL_TYPES.RENT]: "rent",
  [BILL_TYPES.FIXED]: "fixedCharges",
};

export default function MonthlyBillSummary({ flatDetail, billData, dueTypes, cycle, onPayOnline, payingId }) {
  const rentReviewLabel = useRentReviewChip(flatDetail, cycle);

  if (!dueTypes || dueTypes.length === 0) return null;

  // Penalty is derived automatically from the late-payment policy captured at
  // property registration (and copied to the tenant at move-in) — the owner
  // never has to state a penalty amount when uploading the bill itself.
  const lateFeeConfig = {
    latePaymentPolicy: flatDetail?.latePaymentPolicy,
    lateChargesPerDay: flatDetail?.lateChargesPerDay,
    rentRelaxationDays: flatDetail?.rentRelaxationDays,
    flatRent: flatDetail?.flatRent,
  };

  const items = dueTypes.map((type) => {
    const meta = BILL_TYPE_META[type];
    const record = readBillRecord(billData, type);
    const isRent = type === BILL_TYPES.RENT;

    // For rent the cycle is the authority, not the property's monthly figure:
    // it pro-rates a mid-month start and spends the first-month credit, so
    // falling back to raw flatRent would quote a number the tenant never owes.
    const expectedRent =
      cycle?.rentAmount != null ? Number(cycle.rentAmount) : flatDetail?.flatRent
      ? Number(flatDetail.flatRent)
      : null;

    const baseAmount = record?.total_amount
      ? Number(record.total_amount)
      : record?.billAmount
      ? Number(record.billAmount)
      : isRent
      ? expectedRent
      : null;

    const status = record?.approval?.status || (record ? "pending" : null);
    // The cycle's due date is the fallback: a bill with no date of its own is
    // still due on the same day as everything else in that month.
    const dueDateStr = record?.dueDate || record?.due_date || cycle?.dueDate || null;

    // Don't add a penalty to bills that are already approved/paid, or that
    // have no due date to measure lateness against.
    const { daysOverdue, penalty } =
      status === "approved"
        ? { daysOverdue: 0, penalty: 0 }
        : calculateLatePenalty(dueDateStr, lateFeeConfig);

    return {
      type,
      record,
      breakdown: record?.breakdown || [],
      meta,
      baseAmount,
      amount: baseAmount != null ? baseAmount + penalty : null,
      penalty,
      daysOverdue,
      status,
      isRent,
      isExpectedOnly: isRent && !record,
    };
  });

  const grandTotal = items.reduce((sum, item) => sum + (item.amount || 0), 0);
  // The cycle being billed is the month that just ended, not the one we are
  // standing in — a September heading over August's charges was the single
  // most confusing thing on this screen.
  const cycleLabel = cycle?.cycleLabel || dayjs().subtract(1, "month").format("MMMM YYYY");

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        mb: 4,
        borderRadius: 3,
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        color: "#fff",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1, mb: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={800}>
            {cycleLabel} bill
          </Typography>
          {cycle?.dueDate && (
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              Due {cycle.dueDate}
              {cycle.lateSummary
                ? ` · Late charges after ${cycle.deadline}: ${cycle.lateSummary}`
                : ""}
            </Typography>
          )}
        </Box>
        <Typography variant="h5" fontWeight={800}>
          Rs {grandTotal?.toLocaleString()}
        </Typography>
      </Box>

      {/* A mid-month start bills a partial cycle. Stating the days stops the
          tenant wondering why the figure is not the usual one. */}
      {cycle?.isPartial && (
        <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
          This is a partial month — billed for {cycle.occupiedDays} days (out of{" "}
          {cycle.daysInMonth} days total).
          {cycle.rentNote ? ` ${cycle.rentNote}` : ""}
        </Typography>
      )}

      {cycle?.rentEscalated && (
        <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
          Rent has increased from Rs {Number(cycle.baseRent)?.toLocaleString()} to Rs{" "}
          {Number(cycle.rentAmount)?.toLocaleString()} — as per the agreement, a{" "}
          {cycle.rentIncreasePercent}% increase applies every {cycle.leaseTermMonths} months.
        </Typography>
      )}

      {cycle?.rentCreditApplied > 0 && (
        <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
          Rs {cycle.rentCreditApplied?.toLocaleString()} advance-rent credit has
          been deducted from this month's rent.
        </Typography>
      )}

      <Paper elevation={0} sx={{ borderRadius: 2, overflow: "hidden", bgcolor: "rgba(255,255,255,0.97)" }}>
        {items.map((item, index) => (
          <Box key={item.type}>
            {index > 0 && <Divider />}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.5,
                px: 2,
                py: 1.5,
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    bgcolor: item.meta?.bgColor,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "1.1rem",
                  }}
                >
                  {item.meta?.icon}
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 700, color: "#0f172a", fontSize: "0.9rem" }}>
                    {item.meta?.title}
                  </Typography>
                  {item.breakdown.length > 0 && (
                    <Typography sx={{ fontSize: "0.72rem", color: "#64748b", mt: 0.3 }}>
                      {item.breakdown
                        .map((b) => `${b.label} Rs ${Number(b.amount)?.toLocaleString()}`)
                        .join(" · ")}
                    </Typography>
                  )}
                  {item.isRent && rentReviewLabel && (
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.3 }}>
                      <EventRepeatIcon sx={{ fontSize: 14, color: "#7C3AED" }} />
                      <Typography sx={{ fontSize: "0.72rem", color: "#7C3AED", fontWeight: 600 }}>
                        {rentReviewLabel}
                      </Typography>
                    </Stack>
                  )}
                  {item.penalty > 0 && (
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.3 }}>
                      <WarningAmberIcon sx={{ fontSize: 14, color: "#D32F2F" }} />
                      <Typography sx={{ fontSize: "0.72rem", color: "#D32F2F", fontWeight: 600 }}>
                        {item.daysOverdue} day{item.daysOverdue === 1 ? "" : "s"} overdue — +Rs {item.penalty?.toLocaleString()} late fee
                      </Typography>
                    </Stack>
                  )}
                </Box>
              </Box>

              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box sx={{ textAlign: "right" }}>
                  <Typography sx={{ fontWeight: 700, color: item.penalty > 0 ? "#D32F2F" : "#0f172a" }}>
                    {item.amount != null ? `Rs ${item.amount?.toLocaleString()}` : "—"}
                  </Typography>
                  {item.penalty > 0 && (
                    <Typography sx={{ fontSize: "0.7rem", color: "#94a3b8", textDecoration: "line-through" }}>
                      Rs {item.baseAmount?.toLocaleString()}
                    </Typography>
                  )}
                </Box>
                {item.isExpectedOnly ? (
                  <Chip size="small" icon={<InfoOutlinedIcon />} label="Expected" variant="outlined" />
                ) : (
                  statusChip(item.status)
                )}
                {onPayOnline &&
                  ONLINE_PAYABLE[item.type] &&
                  item.record?._id &&
                  item.status !== "approved" && (
                    <Button
                      size="small"
                      variant="contained"
                      disabled={payingId === item.record._id}
                      onClick={() => onPayOnline(ONLINE_PAYABLE[item.type], item.record)}
                    >
                      Pay online
                    </Button>
                  )}
              </Stack>
            </Box>
          </Box>
        ))}
      </Paper>
    </Paper>
  );
}
